// ==========================================
// Theme Service - Fetch and Apply Theme from API
// ==========================================

import { server_api } from '../utils/constants';

/**
 * Resolve image URL - if it's a relative path/endpoint, prepend base URL
 * If it's already a full URL (http/https), return as-is
 * @param {string} url - Image URL or endpoint
 * @returns {string|null} - Resolved full URL or null if no url provided
 */
export const resolveImageUrl = (url) => {
  if (!url) return null;

  // If it's already a full URL (CDN or external), return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // If it's a relative path/endpoint, prepend base URL
  const baseUrl = server_api();
  // Ensure no double slashes when joining
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${baseUrl}${cleanUrl}`;
};

// LocalStorage keys
const THEME_STORAGE_KEY = 'app_theme';
const THEME_DOMAIN_KEY = 'theme_domain'; // For testing with custom domain
const RESTAURANT_ID_KEY = 'CustomerRestaurantId'; // Store restaurant ID from API

// Default theme configuration (fallback when API fails)
export const DEFAULT_THEME = {
  primary: '#667eea',
  secondary: '#764ba2',
  tertiary: '#40E0D0',
  fontColor: '#334155',
  fontName: 'Inter',
  logoUrl: null,
  feviconUrl: null,
  restaurantName: 'Restaurant',
  backgroundColor: '#f8fafc',
  headerColor: '#000000',
  buttonGradient: 'linear-gradient(135deg, #40E0D0 0%, #1cb8b0 100%)',
  buttonHoverGradient: 'linear-gradient(135deg, #1cb8b0 0%, #169c96 100%)',
  // Contact Info
  address: '',
  phone: '',
  alternatePhone: '',
  email: '',
};

/**
 * Extract root domain from hostname (ignores subdomains)
 * Examples:
 *   localhost → localhost
 *   192.168.1.1 → 192.168.1.1
 *   admin.murgaroma.com → murgaroma.com
 *   test.admin.murgaroma.com → murgaroma.com
 *   uat.webdekho.cloud → webdekho.cloud
 * @returns {string} - Root domain name
 */
export const getDomainFromUrl = () => {
  const hostname = window.location.hostname;

  // Handle localhost
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'localhost';
  }

  // Handle IP addresses (IPv4) - return as-is
  const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
  if (ipv4Regex.test(hostname)) {
    return hostname;
  }

  // Split hostname into parts
  const parts = hostname.split('.');

  // If only 2 parts or less (e.g., "example.com"), return as-is
  if (parts.length <= 2) {
    return hostname;
  }

  // Handle common country-code second-level domains (ccSLDs)
  // e.g., .co.uk, .com.au, .co.in, .org.uk, .net.au
  const ccSLDs = ['co', 'com', 'org', 'net', 'gov', 'edu', 'ac', 'or', 'ne'];
  const secondLast = parts[parts.length - 2];

  // Check if it's a ccSLD pattern (e.g., co.uk, com.au)
  if (parts.length >= 3 && ccSLDs.includes(secondLast) && parts[parts.length - 1].length === 2) {
    // Return last 3 parts for ccSLD domains (e.g., example.co.uk)
    return parts.slice(-3).join('.');
  }

  // For standard domains, return last 2 parts (e.g., murgaroma.com)
  return parts.slice(-2).join('.');
};

/**
 * Set custom domain for theme (useful for testing)
 * @param {string} domain - Domain name (e.g., https://tajhotel.com)
 */
export const setThemeDomain = (domain) => {
  if (domain) {
    localStorage.setItem(THEME_DOMAIN_KEY, domain);
    console.log('Theme domain set to:', domain);
  }
};

/**
 * Clear custom domain (will use current URL)
 */
export const clearThemeDomain = () => {
  localStorage.removeItem(THEME_DOMAIN_KEY);
  console.log('Theme domain cleared, using current URL');
};

/**
 * Fetch theme from API based on domain name
 * @param {string} domainName - The domain name to fetch theme for
 * @returns {Promise<Object|null>} - Theme data or null if failed
 */
export const fetchThemeFromApi = async (domainName) => {
  try {
    const baseUrl = server_api();
    const encodedDomain = encodeURIComponent(domainName);
    const apiUrl = `${baseUrl}/api/global/theme/getByDomain?domainName=${encodedDomain}`;

    console.log('Fetching theme from:', apiUrl);

    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const result = await response.json();
      if (result.Status === 'SUCCESS' && result.data) {
        console.log('Theme fetched successfully by domain:', result.data);
        return result.data;
      }
    }

    // Domain lookup failed - try fallback with restaurant ID from localStorage
    const savedRestId = localStorage.getItem(RESTAURANT_ID_KEY);
    if (savedRestId) {
      console.log('Domain lookup failed, trying getByRestId with:', savedRestId);
      const restIdUrl = `${baseUrl}/api/global/theme/getByRestId?restId=${savedRestId}`;
      const restIdResponse = await fetch(restIdUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (restIdResponse.ok) {
        const restIdResult = await restIdResponse.json();
        if (restIdResult.Status === 'SUCCESS' && restIdResult.data) {
          console.log('Theme fetched successfully by restId:', restIdResult.data);
          return restIdResult.data;
        }
      }
    }

    console.warn('Theme API: both domain and restId lookups failed');
    return null;
  } catch (error) {
    console.error('Error fetching theme from API:', error);
    return null;
  }
};

/**
 * Save theme data to localStorage
 * @param {Object} themeData - Theme data to save
 */
export const saveThemeToLocalStorage = (themeData) => {
  try {
    const themeToSave = {
      ...themeData,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(themeToSave));
    console.log('Theme saved to localStorage:', {
      primary: themeToSave.primary,
      secondary: themeToSave.secondary,
      tertiary: themeToSave.tertiary,
    });
  } catch (error) {
    console.error('Error saving theme to localStorage:', error);
  }
};

/**
 * Get theme data from localStorage
 * @returns {Object|null} - Theme data or null if not found
 */
export const getThemeFromLocalStorage = () => {
  try {
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    if (savedTheme) {
      return JSON.parse(savedTheme);
    }
    return null;
  } catch (error) {
    console.error('Error getting theme from localStorage:', error);
    return null;
  }
};

/**
 * Clear theme from localStorage
 */
export const clearThemeFromLocalStorage = () => {
  try {
    localStorage.removeItem(THEME_STORAGE_KEY);
    console.log('Theme cleared from localStorage');
  } catch (error) {
    console.error('Error clearing theme from localStorage:', error);
  }
};

/**
 * Map API response to theme object
 * @param {Object} apiData - API response data
 * @returns {Object} - Mapped theme object
 */
export const mapApiDataToTheme = (apiData) => {
  console.log('Raw API data received:', apiData);

  // Handle both 'primary' and 'primarys' field names from API
  const primaryColor = apiData.primarys || apiData.primary;
  const secondaryColor = apiData.secondary;
  const tertiaryColor = apiData.tertiary;

  console.log('Extracted colors from API:', { primaryColor, secondaryColor, tertiaryColor });

  const mappedTheme = {
    id: apiData.id,
    restaurantId: apiData.restaurantId?.id,
    restaurantName: apiData.restaurantName || DEFAULT_THEME.restaurantName,
    // Use API colors directly, only fallback to DEFAULT if not provided
    primary: primaryColor || DEFAULT_THEME.primary,
    secondary: secondaryColor || DEFAULT_THEME.secondary,
    tertiary: tertiaryColor || DEFAULT_THEME.tertiary,
    fontColor: apiData.fontColour || apiData.fontColor || DEFAULT_THEME.fontColor,
    fontName: apiData.fontName || DEFAULT_THEME.fontName,
    // Prefer driveLogoUrl (CDN) over logoUrl (relative path)
    logoUrl: apiData.driveLogoUrl || resolveImageUrl(apiData.logoUrl) || DEFAULT_THEME.logoUrl,
    // Prefer driveFeviconUrl (CDN) over feviconUrl (relative path)
    feviconUrl: apiData.driveFeviconUrl || resolveImageUrl(apiData.feviconUrl) || DEFAULT_THEME.feviconUrl,
    backgroundColor: apiData.backgroundColor || DEFAULT_THEME.backgroundColor,
    headerColor: apiData.headerColor || DEFAULT_THEME.headerColor,
    buttonGradient: apiData.buttonGradient || DEFAULT_THEME.buttonGradient,
    buttonHoverGradient: apiData.buttonHoverGradient || DEFAULT_THEME.buttonHoverGradient,
    website: apiData.website,
    gstNumber: apiData.gstNumber,
    fssaiNumber: apiData.fssaiNumber,
    // Contact Info
    address: apiData.address || DEFAULT_THEME.address,
    phone: apiData.phone || DEFAULT_THEME.phone,
    alternatePhone: apiData.alternatePhone || DEFAULT_THEME.alternatePhone,
    email: apiData.restaurantId?.email || DEFAULT_THEME.email,
    // Store original API colors separately for reference
    apiColors: {
      primary: primaryColor,
      secondary: secondaryColor,
      tertiary: tertiaryColor,
    },
    // Social Media Details
    socialMediaDetails: apiData.socialMediaDetails || null,
  };

  console.log('Mapped theme object:', mappedTheme);
  return mappedTheme;
};

/**
 * Convert hex color to RGB values
 * @param {string} hex - Hex color (e.g., #667eea)
 * @returns {string} - RGB values as string (e.g., "102, 126, 234")
 */
const hexToRgb = (hex) => {
  // Remove # if present
  const cleanHex = hex.replace('#', '');

  // Parse hex values
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  return `${r}, ${g}, ${b}`;
};

/**
 * Darken a hex color by a percentage
 */
const darkenHex = (hex, percent) => {
  const clean = hex.replace('#', '');
  let r = parseInt(clean.substring(0, 2), 16);
  let g = parseInt(clean.substring(2, 4), 16);
  let b = parseInt(clean.substring(4, 6), 16);
  r = Math.max(0, Math.floor(r * (1 - percent / 100)));
  g = Math.max(0, Math.floor(g * (1 - percent / 100)));
  b = Math.max(0, Math.floor(b * (1 - percent / 100)));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

/**
 * Calculate WCAG relative luminance of a hex color
 * Returns a value between 0 (black) and 1 (white)
 */
const getLuminance = (hex) => {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16) / 255;
  const g = parseInt(clean.substring(2, 4), 16) / 255;
  const b = parseInt(clean.substring(4, 6), 16) / 255;
  const toLinear = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

/**
 * Return #000000 or #ffffff — whichever has better contrast against the given hex color
 */
export const getContrastColor = (hex) => {
  if (!hex || typeof hex !== 'string') return '#ffffff';
  return getLuminance(hex) > 0.179 ? '#000000' : '#ffffff';
};

/**
 * Lighten a hex color by a percentage
 */
const lightenHex = (hex, percent) => {
  const clean = hex.replace('#', '');
  let r = parseInt(clean.substring(0, 2), 16);
  let g = parseInt(clean.substring(2, 4), 16);
  let b = parseInt(clean.substring(4, 6), 16);
  r = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)));
  g = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)));
  b = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

/**
 * Apply theme to CSS variables on document root
 * @param {Object} theme - Theme object to apply
 */
export const applyThemeToCSS = (theme) => {
  const root = document.documentElement;

  // Apply color variables (both hex and RGB for rgba usage)
  if (theme.primary) {
    root.style.setProperty('--theme-primary', theme.primary);
    root.style.setProperty('--theme-primary-rgb', hexToRgb(theme.primary));
    root.style.setProperty('--theme-primary-dark', darkenHex(theme.primary, 15));
    root.style.setProperty('--theme-primary-light', lightenHex(theme.primary, 30));
    // Aliases used by mesh-gradient tint system
    root.style.setProperty('--primary-color', theme.primary);
    root.style.setProperty('--primary-color-rgb', hexToRgb(theme.primary));
    // Auto contrast: #000 on light primaries, #fff on dark primaries
    root.style.setProperty('--primary-color-contrast', getContrastColor(theme.primary));
  }
  if (theme.secondary) {
    root.style.setProperty('--theme-secondary', theme.secondary);
    root.style.setProperty('--theme-secondary-rgb', hexToRgb(theme.secondary));
  }
  if (theme.tertiary) {
    root.style.setProperty('--theme-tertiary', theme.tertiary);
    root.style.setProperty('--theme-tertiary-rgb', hexToRgb(theme.tertiary));
  }
  if (theme.fontColor) {
    root.style.setProperty('--theme-font-color', theme.fontColor);
    root.style.setProperty('--theme-font-color-rgb', hexToRgb(theme.fontColor));
  }
  // Compute tinted backgrounds — always driven by primary color so the
  // whole UI gets a visible wash of the primary hue.
  if (theme.primary) {
    const _p  = theme.primary.replace('#', '');
    const _pr = parseInt(_p.substring(0, 2), 16);
    const _pg = parseInt(_p.substring(2, 4), 16);
    const _pb = parseInt(_p.substring(4, 6), 16);
    const toHex = (c) => Math.min(255, Math.max(0, Math.round(c))).toString(16).padStart(2, '0');

    // Light-mode tinted bg: blend primary at 8% over #f8fafc (248, 250, 252)
    const lightBg = '#' + [
      248 * 0.92 + _pr * 0.08,
      250 * 0.92 + _pg * 0.08,
      252 * 0.92 + _pb * 0.08,
    ].map(toHex).join('');
    root.style.setProperty('--theme-background', lightBg);

    // Dark-mode tinted bg: blend primary at 10% over #0e0e16 (14, 14, 22)
    const darkBg = '#' + [
      14 * 0.90 + _pr * 0.10,
      14 * 0.90 + _pg * 0.10,
      22 * 0.90 + _pb * 0.10,
    ].map(toHex).join('');
    root.style.setProperty('--theme-background-dark', darkBg);
  }
  if (theme.headerColor) {
    root.style.setProperty('--theme-header-color', theme.headerColor);
  }

  // Apply button gradients
  if (theme.buttonGradient) {
    root.style.setProperty('--theme-button-color', theme.buttonGradient);
  }
  if (theme.buttonHoverGradient) {
    root.style.setProperty('--theme-button-hover', theme.buttonHoverGradient);
  }

  // Apply font family
  if (theme.fontName) {
    root.style.setProperty('--theme-font-family', `'${theme.fontName}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`);
    document.body.style.fontFamily = `'${theme.fontName}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
  }
  // Apply font size
  if (theme.fontSize) {
    root.style.setProperty('--theme-font-size', theme.fontSize);
    document.body.style.fontSize = theme.fontSize;
  }
  // Apply font weight
  if (theme.fontWeight) {
    root.style.setProperty('--theme-font-weight', theme.fontWeight);
    document.body.style.fontWeight = theme.fontWeight;
  }

  // Apply favicon (falls back to /favicon.ico)
  updateFavicon(theme.feviconUrl || null);

  // Update browser tab title
  document.title = theme.restaurantName
    ? `Webdekho 🍽 ${theme.restaurantName}`
    : 'Webdekho 🍽 RMS';

  console.log('Theme applied to CSS variables');
};

/**
 * Update favicon dynamically
 * @param {string} faviconUrl - URL of the favicon
 */
export const updateFavicon = (faviconUrl) => {
  try {
    const href = faviconUrl || '/favicon.ico';

    // Chrome/Safari cache favicons aggressively — just mutating an existing
    // <link>'s href often does not trigger a re-fetch. Remove every favicon
    // link and append fresh ones so the browser re-evaluates from scratch.
    document
      .querySelectorAll("link[rel='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon']")
      .forEach((el) => el.remove());

    const isData = href.startsWith('data:');
    const cacheBusted = isData ? href : `${href}${href.includes('?') ? '&' : '?'}v=${Date.now()}`;

    const icon = document.createElement('link');
    icon.rel = 'icon';
    icon.href = cacheBusted;
    document.head.appendChild(icon);

    const apple = document.createElement('link');
    apple.rel = 'apple-touch-icon';
    apple.href = cacheBusted;
    document.head.appendChild(apple);

    console.log('Favicon updated:', cacheBusted);
  } catch (error) {
    console.error('Error updating favicon:', error);
  }
};

/**
 * Fetch theme by restaurant ID directly
 * Used when a user is logged in and we know their restaurant ID
 */
const fetchThemeByRestId = async (restId) => {
  try {
    const baseUrl = server_api();
    const apiUrl = `${baseUrl}/api/global/theme/getByRestId?restId=${restId}`;
    console.log('Fetching theme by restId:', apiUrl);
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (response.ok) {
      const result = await response.json();
      if (result.Status === 'SUCCESS' && result.data) {
        console.log('Theme fetched successfully by restId:', result.data);
        return result.data;
      }
    }
    return null;
  } catch (error) {
    console.error('Error fetching theme by restId:', error);
    return null;
  }
};

/**
 * Initialize theme - main function to call on app load
 * Fetches from API, saves to localStorage, and applies to CSS
 * Falls back to localStorage or default theme if API fails
 *
 * Priority: logged-in user's restaurant ID > domain lookup > cached > default
 */
export const initializeTheme = async () => {
  try {
    const domainName = getDomainFromUrl();
    console.log('Initializing theme for domain:', domainName);

    // If a user is logged in, prefer their restaurant's theme over domain-based lookup
    // This ensures impersonated/restaurant users see their own theme on shared domains
    let apiData = null;
    const loggedInUser = localStorage.getItem('user');
    if (loggedInUser) {
      try {
        const user = JSON.parse(loggedInUser);
        const userId = user?.id || localStorage.getItem('UserId');
        // userType from login, role from impersonation/refresh
        const userRole = user?.userType || user?.role || localStorage.getItem('UserRole');
        // Resolve the restaurant this user belongs to so all staff roles of
        // the same restaurant see identical branding (primary color + logo).
        //  • restaurant: the user's own ID is the restaurant ID.
        //  • branch/kitchen/cashier/captain: parentId points at the restaurant
        //    (branches may be nested one level deeper).
        const STAFF_ROLES = ['restaurant', 'branch', 'kitchen', 'cashier', 'captain'];
        if (userId && STAFF_ROLES.includes(userRole)) {
          // parentId: flat ID from login, or nested object from other flows
          const parentId = user?.parentId?.id || user?.parentId || user?.branchId?.id;
          const restId = userRole === 'restaurant' ? userId : (parentId || userId);
          if (restId) {
            console.log('Logged-in user detected, fetching theme by restId:', restId);
            apiData = await fetchThemeByRestId(restId);
          }
        }
      } catch (e) {
        console.warn('Could not parse logged-in user for theme lookup:', e);
      }
    }

    // Fall back to domain-based lookup if no user-specific theme found
    if (!apiData) {
      apiData = await fetchThemeFromApi(domainName);
    }

    if (apiData) {
      // API succeeded - map data, save to localStorage, and apply
      console.log('API data received successfully, mapping theme...');
      const theme = mapApiDataToTheme(apiData);

      // Save restaurantId to localStorage
      if (apiData.restaurantId?.id) {
        localStorage.setItem(RESTAURANT_ID_KEY, apiData.restaurantId.id);
        console.log('Restaurant ID saved to localStorage:', apiData.restaurantId.id);
      }

      // Clear old theme first to ensure fresh data
      clearThemeFromLocalStorage();

      // Save new theme from API
      saveThemeToLocalStorage(theme);

      // Apply theme with primary color as main theme color
      applyThemeToCSS(theme);

      console.log('Theme initialized from API with colors:', {
        primary: theme.primary,
        secondary: theme.secondary,
        tertiary: theme.tertiary,
      });

      return theme;
    } else {
      // API failed - try to use cached theme from localStorage
      console.log('API failed, trying localStorage...');
      const cachedTheme = getThemeFromLocalStorage();

      if (cachedTheme) {
        console.log('Using cached theme from localStorage');
        applyThemeToCSS(cachedTheme);
        return cachedTheme;
      } else {
        // No cached theme - use default
        console.log('No cached theme, using default theme');
        applyThemeToCSS(DEFAULT_THEME);
        return DEFAULT_THEME;
      }
    }
  } catch (error) {
    console.error('Error initializing theme:', error);
    // On any error, apply default theme
    applyThemeToCSS(DEFAULT_THEME);
    return DEFAULT_THEME;
  }
};

/**
 * Get current theme (from localStorage or default)
 * @returns {Object} - Current theme
 */
export const getCurrentTheme = () => {
  const savedTheme = getThemeFromLocalStorage();
  return savedTheme || DEFAULT_THEME;
};

/**
 * Force refresh theme from API
 * Useful when user wants to get latest theme
 */
export const refreshTheme = async () => {
  clearThemeFromLocalStorage();
  return await initializeTheme();
};

/**
 * Get restaurant ID from localStorage
 * @returns {string|null} - Restaurant ID or null if not found
 */
export const getRestaurantId = () => {
  return localStorage.getItem(RESTAURANT_ID_KEY);
};
